import { Alert } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { Link } from "react-router";
import type { Bookability } from "./bookability";

const fixBtn = css({
  colorPalette: "brand",
  bg: "colorPalette.default",
  color: "colorPalette.fg",
  px: "4",
  py: "2",
  borderRadius: "md",
  fontWeight: "semibold",
  fontSize: "sm",
  cursor: "pointer",
  w: "fit-content",
  _disabled: { opacity: 0.5, cursor: "not-allowed" },
});
const continueBtn = css({
  px: "4",
  py: "2",
  borderRadius: "md",
  borderWidth: "1px",
  borderColor: "border.default",
  fontSize: "sm",
  cursor: "pointer",
  w: "fit-content",
  _disabled: { opacity: 0.5, cursor: "not-allowed" },
});

/**
 * Guests can't book anything at this venue yet. The primary action fixes it: inline when the caller can
 * edit hours in place, otherwise in the full editor. `onContinue` is the go-live panel's explicit choice;
 * UI-only by design, `POST /go-live` still accepts a venue with no hours (OnboardingProgress).
 */
export function NoBookableTimesAlert({
  venueId,
  bookability,
  busy = false,
  onFixHours,
  onContinue,
}: {
  venueId: string;
  bookability: Exclude<Bookability, "OK">;
  busy?: boolean;
  onFixHours?: () => void;
  onContinue?: () => void;
}) {
  const { t } = useTranslation();
  const inlineFix = bookability === "NO_HOURS" ? onFixHours : undefined;
  return (
    <Alert.Root status="warning" role="alert">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>{t("onboarding.activate.noBookable.title")}</Alert.Title>
        <Alert.Description>{t(`onboarding.activate.noBookable.${bookability}`)}</Alert.Description>
        <div
          className={css({
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "3",
            mt: "2",
          })}
        >
          {inlineFix ? (
            <button type="button" disabled={busy} onClick={inlineFix} className={fixBtn}>
              {t("onboarding.activate.noBookable.fixHours")}
            </button>
          ) : (
            <Link to={`/v/${venueId}/schedule`} className={fixBtn}>
              {t(
                bookability === "NO_HOURS"
                  ? "onboarding.activate.noBookable.fixHours"
                  : "onboarding.activate.noBookable.fixInEditor",
              )}
            </Link>
          )}
          {onContinue && (
            <button type="button" disabled={busy} onClick={onContinue} className={continueBtn}>
              {busy
                ? t("onboarding.activate.working")
                : t("onboarding.activate.noBookable.continue")}
            </button>
          )}
        </div>
      </Alert.Content>
    </Alert.Root>
  );
}
